import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  fetchReferrals,
  fetchReferralById,
  createReferral,
  updateReferral,
  deleteReferral,
  fetchReferralPatients,
} from "../../services/referralApi";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { GetAllCountries2 } from "../../reducer/Countries";
import CountryCodeSelect from "../Enquiry/CountryCodeSelect";
import CloseIcon from "@mui/icons-material/Close";
import "./Referrals.css";

export default function Referrals() {
  const navigate = useNavigate();
  const userRole = (localStorage.getItem("Role") || "").toUpperCase();
  const isReferralUser = userRole === "REFERRAL";
  const myReferralId = localStorage.getItem("referral_id") || "";
  const myName = localStorage.getItem("name") || "Referral Partner";

  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const dispatch = useDispatch();
  const { Countries } = useSelector((state) => state.Countries || {});

  useEffect(() => {
    if (!Countries || Countries.length === 0) {
      dispatch(GetAllCountries2());
    }
  }, [dispatch, Countries]);

  // Add / Edit Modal State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentReferral, setCurrentReferral] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    referral_id: "",
    name: "",
    phone_code: "",
    phone: "",
    email: "",
    password: "",
    status: 1,
  });

  // Details & Linked Patients Modal State
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailReferral, setDetailReferral] = useState(null);
  const [linkedPatients, setLinkedPatients] = useState([]);
  const [linkedPatientsCount, setLinkedPatientsCount] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [patientSearch, setPatientSearch] = useState("");

  // Load Referrals list (for Admin / Staff)
  const loadReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchReferrals({
        page,
        limit,
        search,
        status: statusFilter === "ALL" ? undefined : Number(statusFilter),
      });

      if (res && res.success) {
        setReferrals(Array.isArray(res.referrals) ? res.referrals : []);
        setTotal(res.total || 0);
      } else {
        setReferrals([]);
        setTotal(0);
      }
    } catch (err) {
      console.error("Failed to load referrals:", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to load referrals", "error");
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  // Load Partner Portal data (for Referral partner)
  const loadPartnerData = useCallback(async () => {
    setLoading(true);
    try {
      if (myReferralId) {
        const [detailRes, patientsRes] = await Promise.allSettled([
          fetchReferralById(myReferralId),
          fetchReferralPatients(myReferralId),
        ]);

        if (detailRes.status === "fulfilled" && detailRes.value?.success) {
          setDetailReferral(detailRes.value.referral);
          setLinkedPatientsCount(detailRes.value.linkedPatientsCount || 0);
        }
        if (patientsRes.status === "fulfilled" && patientsRes.value?.success) {
          setLinkedPatients(patientsRes.value.patients || []);
          if (patientsRes.value.totalPatients !== undefined) {
            setLinkedPatientsCount(patientsRes.value.totalPatients);
          }
        }
      }
    } catch (err) {
      console.warn("Failed to load partner data:", err);
    } finally {
      setLoading(false);
    }
  }, [myReferralId]);

  useEffect(() => {
    if (isReferralUser) {
      loadPartnerData();
    } else {
      loadReferrals();
    }
  }, [isReferralUser, loadPartnerData, loadReferrals]);

  // Debounced or on-submit search
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  // Open Add Modal
  const handleOpenAdd = () => {
    setIsEditing(false);
    setCurrentReferral(null);
    setFormData({
      referral_id: "",
      name: "",
      phone_code: "",
      phone: "",
      email: "",
      password: "",
      status: 1,
    });
    setIsFormOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (refItem) => {
    setIsEditing(true);
    setCurrentReferral(refItem);
    setFormData({
      referral_id: refItem.referral_id || "",
      name: refItem.name || "",
      phone_code: refItem.phone_code || "",
      phone: refItem.phone || "",
      email: refItem.email || "",
      password: "", // Leave blank if not updating
      status: refItem.status !== undefined ? Number(refItem.status) : 1,
    });
    setIsFormOpen(true);
  };

  // Save Referral (Create or Update)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      Swal.fire("Validation Error", "Name is required", "warning");
      return;
    }
    if (!formData.phone_code.trim()) {
      Swal.fire("Validation Error", "Please select a country code", "warning");
      return;
    }
    if (!formData.phone.trim()) {
      Swal.fire("Validation Error", "Phone number is required", "warning");
      return;
    }
    if (!isEditing && !formData.password.trim()) {
      Swal.fire("Validation Error", "Password is required for new referral partner", "warning");
      return;
    }

    setFormSubmitting(true);
    try {
      if (isEditing) {
        const updatePayload = {
          name: formData.name.trim(),
          phone_code: formData.phone_code.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          status: Number(formData.status),
        };
        if (formData.password.trim()) {
          updatePayload.password = formData.password.trim();
        }

        const identifier = currentReferral?.referral_id || currentReferral?.id;
        const res = await updateReferral(identifier, updatePayload);
        if (res && res.success) {
          Swal.fire("Success", res.message || "Referral partner updated successfully", "success");
          setIsFormOpen(false);
          loadReferrals();
        } else {
          Swal.fire("Error", res.message || "Failed to update referral partner", "error");
        }
      } else {
        const createPayload = {
          name: formData.name.trim(),
          phone_code: formData.phone_code.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          password: formData.password.trim(),
          status: Number(formData.status),
        };
        if (formData.referral_id.trim()) {
          createPayload.referral_id = formData.referral_id.trim();
        }

        const res = await createReferral(createPayload);
        if (res && res.success) {
          Swal.fire("Success", res.message || "Referral partner created successfully", "success");
          setIsFormOpen(false);
          loadReferrals();
        } else {
          Swal.fire("Error", res.message || "Failed to create referral partner", "error");
        }
      }
    } catch (err) {
      console.error("Save referral error:", err);
      const errMsg =
        err.response?.data?.message || err.message || "An error occurred while saving referral";
      Swal.fire("Error", errMsg, "error");
    } finally {
      setFormSubmitting(false);
    }
  };

  // Delete Referral
  const handleDelete = (refItem) => {
    const identifier = refItem.referral_id || refItem.id;
    Swal.fire({
      title: "Delete Referral Partner?",
      text: `Are you sure you want to delete ${refItem.name} (${refItem.referral_id || refItem.id})? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await deleteReferral(identifier);
          if (res && res.success) {
            Swal.fire("Deleted!", res.message || "Referral partner deleted successfully.", "success");
            loadReferrals();
          } else {
            Swal.fire("Error", res.message || "Failed to delete referral partner", "error");
          }
        } catch (err) {
          console.error("Delete referral error:", err);
          Swal.fire("Error", err.response?.data?.message || "Failed to delete referral partner", "error");
        }
      }
    });
  };

  // Toggle status directly from table
  const handleToggleStatus = async (refItem) => {
    const newStatus = refItem.status === 1 ? 0 : 1;
    const identifier = refItem.referral_id || refItem.id;
    try {
      const res = await updateReferral(identifier, { status: newStatus });
      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Status Updated",
          text: `${refItem.name} marked as ${newStatus === 1 ? "Active" : "Inactive"}`,
          timer: 1500,
          showConfirmButton: false,
        });
        loadReferrals();
      } else {
        Swal.fire("Error", res.message || "Failed to update status", "error");
      }
    } catch (err) {
      console.error("Toggle status error:", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to update status", "error");
    }
  };

  // View Details & Linked Patients
  const handleOpenDetails = async (refItem) => {
    setIsDetailOpen(true);
    setDetailLoading(true);
    setDetailReferral(refItem);
    setLinkedPatients([]);
    setLinkedPatientsCount(0);
    setPatientSearch("");

    const identifier = refItem.referral_id || refItem.id;
    try {
      // 1. Fetch details
      const detailRes = await fetchReferralById(identifier);
      if (detailRes && detailRes.success && detailRes.referral) {
        setDetailReferral(detailRes.referral);
        setLinkedPatientsCount(detailRes.linkedPatientsCount || 0);
      }

      // 2. Fetch linked patients list
      const patientsRes = await fetchReferralPatients(refItem.referral_id);
      if (patientsRes && patientsRes.success && Array.isArray(patientsRes.patients)) {
        setLinkedPatients(patientsRes.patients);
        if (patientsRes.totalPatients !== undefined) {
          setLinkedPatientsCount(patientsRes.totalPatients);
        }
      }
    } catch (err) {
      console.warn("Error fetching details/patients:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Filtered linked patients
  const filteredPatients = useMemo(() => {
    if (!patientSearch.trim()) return linkedPatients;
    const q = patientSearch.toLowerCase();
    return linkedPatients.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) ||
        p.omca_patient_id?.toLowerCase().includes(q) ||
        p.phone?.toLowerCase().includes(q) ||
        p.email?.toLowerCase().includes(q) ||
        p.passport_number?.toLowerCase().includes(q)
    );
  }, [linkedPatients, patientSearch]);

  // Compute Active/Inactive stats from current listing
  const activeCount = useMemo(() => {
    return referrals.filter((r) => r.status === 1).length;
  }, [referrals]);
  const inactiveCount = useMemo(() => {
    return referrals.filter((r) => r.status === 0).length;
  }, [referrals]);

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className="page-wrapper">
      <div className="content referrals-page">
        {isReferralUser ? (
          <div>
            {/* Header Card */}
            <div className="referral-header-card d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h3 className="referral-title">
                  <i className="fa fa-handshake-o text-success" /> Referral Partner Portal
                </h3>
                <p className="referral-subtitle">
                  Welcome, <strong>{detailReferral?.name || myName}</strong>! Track your registered patients and submit new patient enquiries.
                </p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-success"
                  onClick={() => navigate("/Admin/add-Enquiry")}
                >
                  <i className="fa fa-user-plus me-1" /> Refer New Patient
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={loadPartnerData}
                  title="Refresh Patients"
                >
                  <i className={`fa fa-refresh ${loading ? "fa-spin" : ""}`} />
                </button>
              </div>
            </div>

            {/* Summary Banner */}
            <div className="referral-detail-banner">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <h4>{detailReferral?.name || myName}</h4>
                  <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                    <span className="badge bg-light text-dark font-monospace">
                      Referral ID: {detailReferral?.referral_id || myReferralId || "—"}
                    </span>
                    {(detailReferral?.phone || localStorage.getItem("phone")) && (
                      <span>
                        <i className="fa fa-phone me-1" />
                        {detailReferral?.phone_code ? `${detailReferral.phone_code} ` : ""}
                        {detailReferral?.phone || localStorage.getItem("phone")}
                      </span>
                    )}
                    {detailReferral?.email && (
                      <span>
                        <i className="fa fa-envelope me-1" />
                        {detailReferral.email}
                      </span>
                    )}
                    <span className="badge bg-success">Active Partner</span>
                  </div>
                </div>
                <div className="text-center bg-white bg-opacity-25 p-2 px-4 rounded">
                  <div className="fs-2 fw-bold">{linkedPatientsCount}</div>
                  <div className="small">Patients Referred</div>
                </div>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="referral-filter-bar d-flex flex-wrap align-items-center justify-content-between gap-3">
              <h5 className="mb-0 fw-bold">
                <i className="fa fa-wheelchair text-success me-2" />
                Your Registered Patients ({linkedPatients.length})
              </h5>
              <div className="search-input-wrapper" style={{ minWidth: 260 }}>
                <i className="fa fa-search" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter your patients..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Patients Table */}
            <div className="referral-table-container">
              {loading ? (
                <div className="text-center py-5">
                  <CircularProgress size={36} />
                  <div className="mt-2 text-muted">Loading your patients...</div>
                </div>
              ) : filteredPatients.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fa fa-users fa-3x mb-2 text-secondary" />
                  <p className="mb-2">
                    {linkedPatients.length === 0
                      ? "You haven't referred any patients yet."
                      : "No matching patients found."}
                  </p>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => navigate("/Admin/add-Enquiry")}
                  >
                    + Refer Your First Patient
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table referral-table">
                    <thead>
                      <tr>
                        <th>Patient Name</th>
                        <th>OMCA Patient ID</th>
                        <th>Contact Number</th>
                        <th>Passport Number</th>
                        <th>Age</th>
                        <th>Registered Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPatients.map((pt) => (
                        <tr key={pt.id || pt.omca_patient_id}>
                          <td>
                            <strong>{pt.name}</strong>
                            {pt.email && (
                              <div className="text-muted small">{pt.email}</div>
                            )}
                          </td>
                          <td>
                            <span className="badge bg-secondary font-monospace">
                              {pt.omca_patient_id || `PT-${pt.id}`}
                            </span>
                          </td>
                          <td>{pt.phone || "—"}</td>
                          <td>{pt.passport_number || "—"}</td>
                          <td>{pt.age || "—"}</td>
                          <td>
                            {pt.created_at
                              ? new Date(pt.created_at).toLocaleDateString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Header Card */}
            <div className="referral-header-card d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                <h3 className="referral-title">
                  <i className="fa fa-handshake-o text-success" /> Referral Management
                </h3>
                <p className="referral-subtitle">
                  Manage referral partners, monitor linked patients, and track referral commissions.
                </p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <button className="btn btn-success" onClick={handleOpenAdd}>
                  <i className="fa fa-plus me-1" /> Add New Referral
                </button>
              </div>
            </div>

            {/* Stats and Filter Bar */}
            <div className="referral-filter-bar d-flex flex-wrap align-items-center justify-content-between gap-3">
              {/* Quick Stats */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="referral-stat-pill total">
                  <i className="fa fa-users" /> Total: {total}
                </span>
                <span className="referral-stat-pill active">
                  <i className="fa fa-check-circle" /> Active: {activeCount}
                </span>
                <span className="referral-stat-pill inactive">
                  <i className="fa fa-times-circle" /> Inactive: {inactiveCount}
                </span>
              </div>

              {/* Search and Status Filters */}
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <form onSubmit={handleSearchSubmit} className="d-flex align-items-center gap-1">
                  <div className="search-input-wrapper">
                    <i className="fa fa-search" />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, phone, email, ID..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                  <button type="submit" className="btn btn-outline-secondary btn-sm" title="Search">
                    Search
                  </button>
                  {search && (
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={handleClearSearch}
                      title="Clear Search"
                    >
                      <i className="fa fa-times" />
                    </button>
                  )}
                </form>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 140, height: 40 }}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="ALL">All Status</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>

                <button
                  className="btn btn-outline-secondary"
                  onClick={loadReferrals}
                  title="Refresh Listing"
                  style={{ height: 40 }}
                >
                  <i className={`fa fa-refresh ${loading ? "fa-spin" : ""}`} />
                </button>
              </div>
            </div>

            {/* Referrals Table */}
            <div className="referral-table-container">
              <div className="table-responsive">
                <table className="table referral-table">
                  <thead>
                    <tr>
                      <th>Referral ID</th>
                      <th>Partner Name</th>
                      <th>Contact Info</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Linked Patients</th>
                      <th>Joined Date</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-5">
                          <CircularProgress size={36} />
                          <div className="mt-2 text-muted">Loading referrals...</div>
                        </td>
                      </tr>
                    ) : referrals.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-5 text-muted">
                          <i className="fa fa-folder-open-o fa-3x mb-2 text-secondary" />
                          <p className="mb-2">No referral partners found.</p>
                          <button className="btn btn-sm btn-outline-success" onClick={handleOpenAdd}>
                            + Add First Referral Partner
                          </button>
                        </td>
                      </tr>
                    ) : (
                      referrals.map((item) => {
                        const isActive = item.status === 1 || item.status === "1";
                        return (
                          <tr key={item.id || item.referral_id}>
                            <td>
                              <span className="referral-id-tag">
                                {item.referral_id || `REF-${item.id}`}
                              </span>
                            </td>
                            <td>
                              <strong>{item.name}</strong>
                              {item.role && (
                                <div className="text-muted small">Role: {item.role}</div>
                              )}
                            </td>
                            <td>
                              <div>
                                <i className="fa fa-phone text-muted me-1" />
                                {item.phone_code ? `${item.phone_code} ` : ""}
                                {item.phone}
                              </div>
                            </td>
                            <td>
                              {item.email ? (
                                <a href={`mailto:${item.email}`} className="text-decoration-none">
                                  {item.email}
                                </a>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              <Tooltip title="Click to toggle status">
                                <span
                                  className={`status-pill ${isActive ? "active" : "inactive"}`}
                                  onClick={() => handleToggleStatus(item)}
                                >
                                  <i className={`fa fa-circle text-${isActive ? "success" : "danger"}`} />
                                  {isActive ? "Active" : "Inactive"}
                                </span>
                              </Tooltip>
                            </td>
                            <td>
                              <span
                                className="linked-patients-badge"
                                onClick={() => handleOpenDetails(item)}
                                title="View registered patients"
                              >
                                <i className="fa fa-wheelchair" />
                                View Patients
                              </span>
                            </td>
                            <td>
                              {item.created_at
                                ? new Date(item.created_at).toLocaleDateString()
                                : "—"}
                            </td>
                            <td className="text-end">
                              <div className="action-btn-group justify-content-end">
                                <Tooltip title="View Details & Patients">
                                  <button
                                    className="action-btn-circle view"
                                    onClick={() => handleOpenDetails(item)}
                                  >
                                    <i className="fa fa-eye" />
                                  </button>
                                </Tooltip>
                                <Tooltip title="Edit Referral Partner">
                                  <button
                                    className="action-btn-circle edit"
                                    onClick={() => handleOpenEdit(item)}
                                  >
                                    <i className="fa fa-pencil" />
                                  </button>
                                </Tooltip>
                                <Tooltip title="Delete Referral">
                                  <button
                                    className="action-btn-circle delete"
                                    onClick={() => handleDelete(item)}
                                  >
                                    <i className="fa fa-trash" />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!loading && referrals.length > 0 && (
                <div className="d-flex flex-wrap align-items-center justify-content-between p-3 border-top gap-2">
                  <div className="text-muted small">
                    Showing {referrals.length} of {total} referral partners (Page {page} of {totalPages})
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 85 }}
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <i className="fa fa-chevron-left" /> Prev
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next <i className="fa fa-chevron-right" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ----------------- ADD / EDIT REFERRAL MODAL ----------------- */}
        <Dialog
          open={isFormOpen}
          onClose={() => !formSubmitting && setIsFormOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={handleFormSubmit}>
            <DialogTitle className="d-flex justify-content-between align-items-center border-bottom pb-2">
              <span className="fw-bold">
                <i className={`fa ${isEditing ? "fa-pencil" : "fa-user-plus"} text-success me-2`} />
                {isEditing ? "Edit Referral Partner" : "Add New Referral Partner"}
              </span>
              <IconButton
                size="small"
                onClick={() => setIsFormOpen(false)}
                disabled={formSubmitting}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent className="pt-3">
              <div className="row g-3">
                {/* Referral ID */}
                <div className="col-12">
                  <TextField
                    label="Referral ID (Code)"
                    placeholder="Auto-generated e.g. REF1005 if blank"
                    fullWidth
                    size="small"
                    value={formData.referral_id}
                    disabled={isEditing}
                    onChange={(e) => setFormData({ ...formData, referral_id: e.target.value })}
                    helperText={
                      isEditing
                        ? "Referral ID cannot be changed"
                        : "Optional. Leave blank to auto-generate (REF1001, REF1002...)"
                    }
                  />
                </div>

                {/* Partner Name */}
                <div className="col-12">
                  <TextField
                    label="Full Name"
                    placeholder="e.g. Dr. John Doe"
                    fullWidth
                    size="small"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                {/* Phone Number with Country Code */}
                <div className="col-12">
                  <label
                    className="form-label mb-1"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#64748b",
                      display: "block",
                    }}
                  >
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <div className="country-code" style={{ position: "relative" }}>
                    <CountryCodeSelect
                      value={formData.phone_code}
                      onChange={(code) => setFormData({ ...formData, phone_code: code })}
                      countries={Countries}
                      placeholder="+Code"
                    />
                    <input
                      type="tel"
                      className="form-control code-in"
                      placeholder="10-digit mobile number"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      style={{ height: "38px", fontSize: "14px" }}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="col-12">
                  <TextField
                    label="Email Address"
                    placeholder="e.g. partner@example.com"
                    type="email"
                    fullWidth
                    size="small"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                {/* Password */}
                <div className="col-12">
                  <TextField
                    label={isEditing ? "Password (Leave blank to keep current)" : "Password *"}
                    placeholder="Password for partner portal login"
                    type="password"
                    fullWidth
                    size="small"
                    required={!isEditing}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </div>

                {/* Status */}
                <div className="col-12">
                  <TextField
                    select
                    label="Account Status"
                    fullWidth
                    size="small"
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: Number(e.target.value) })
                    }
                  >
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </TextField>
                </div>
              </div>
            </DialogContent>

            <DialogActions className="border-top p-3">
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setIsFormOpen(false)}
                disabled={formSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={formSubmitting}
                startIcon={formSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {formSubmitting
                  ? "Saving..."
                  : isEditing
                  ? "Update Referral"
                  : "Create Referral"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ----------------- DETAILS & LINKED PATIENTS MODAL ----------------- */}
        <Dialog
          open={isDetailOpen}
          onClose={() => setIsDetailOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle className="d-flex justify-content-between align-items-center border-bottom pb-2">
            <span className="fw-bold">
              <i className="fa fa-info-circle text-info me-2" />
              Referral Partner Details & Linked Patients
            </span>
            <IconButton size="small" onClick={() => setIsDetailOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent className="pt-3">
            {detailLoading ? (
              <div className="text-center py-5">
                <CircularProgress size={32} />
                <p className="mt-2 text-muted">Loading details & patients...</p>
              </div>
            ) : (
              detailReferral && (
                <>
                  {/* Summary Banner */}
                  <div className="referral-detail-banner">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                      <div>
                        <h4>{detailReferral.name}</h4>
                        <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                          <span className="badge bg-light text-dark font-monospace">
                            ID: {detailReferral.referral_id || `REF-${detailReferral.id}`}
                          </span>
                          <span>
                            <i className="fa fa-phone me-1" />
                            {detailReferral.phone_code ? `${detailReferral.phone_code} ` : ""}
                            {detailReferral.phone}
                          </span>
                          {detailReferral.email && (
                            <span>
                              <i className="fa fa-envelope me-1" />
                              {detailReferral.email}
                            </span>
                          )}
                          <span
                            className={`badge ${
                              detailReferral.status === 1 ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {detailReferral.status === 1 ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </div>
                      <div className="text-center bg-white bg-opacity-25 p-2 px-3 rounded">
                        <div className="fs-3 fw-bold">{linkedPatientsCount}</div>
                        <div className="small">Linked Patients</div>
                      </div>
                    </div>
                  </div>

                  {/* Linked Patients Section */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <h5 className="mb-0 fw-bold">
                      <i className="fa fa-wheelchair text-success me-2" />
                      Patients Registered Under This Referrer ({linkedPatients.length})
                    </h5>
                    {linkedPatients.length > 0 && (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{ maxWidth: 220 }}
                        placeholder="Search patient..."
                        value={patientSearch}
                        onChange={(e) => setPatientSearch(e.target.value)}
                      />
                    )}
                  </div>

                  {filteredPatients.length === 0 ? (
                    <div className="text-center py-4 bg-light rounded text-muted">
                      <i className="fa fa-user-times fa-2x mb-2 text-secondary" />
                      <p className="mb-0">
                        {linkedPatients.length === 0
                          ? "No patients have been registered through this referral partner yet."
                          : "No matching patients found."}
                      </p>
                    </div>
                  ) : (
                    <div className="table-responsive border rounded">
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Patient Name</th>
                            <th>Patient ID</th>
                            <th>Phone</th>
                            <th>Passport</th>
                            <th>Age</th>
                            <th>Registered On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPatients.map((pt) => (
                            <tr key={pt.id || pt.omca_patient_id}>
                              <td>
                                <strong>{pt.name}</strong>
                                {pt.email && (
                                  <div className="text-muted small">{pt.email}</div>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-secondary font-monospace">
                                  {pt.omca_patient_id || `PT-${pt.id}`}
                                </span>
                              </td>
                              <td>{pt.phone}</td>
                              <td>{pt.passport_number || "—"}</td>
                              <td>{pt.age || "—"}</td>
                              <td>
                                {pt.created_at
                                  ? new Date(pt.created_at).toLocaleDateString()
                                  : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )
            )}
          </DialogContent>

          <DialogActions className="border-top p-2 px-3">
            <Button variant="contained" color="inherit" onClick={() => setIsDetailOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
